import * as React from 'react'
import { tw } from 'utils/classnames'

type SlideDirection = 'left' | 'right' | 'up-right'

/**
 * The two copies are given different jobs: the leaving icon accelerates out and clears the slot with
 * momentum, the arriving one takes longer, starts a beat later and settles just past its mark.
 * Matching their speed and curve is what makes the pair read as one object dragged across.
 */
const LEAVING =
  'transition-transform duration-200 ease-[cubic-bezier(0.4,0,1,1)] motion-reduce:transition-none'
const ARRIVING =
  'transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] motion-reduce:transition-none group-[:is(:hover,:focus-visible)]/interactive:delay-[60ms]'

/**
 * A diagonal arrow has to travel on both axes or the glyph and its motion disagree — it leaves
 * along the line it points down, and its replacement enters from the opposite corner.
 */
const SLIDE: Record<SlideDirection, { leaving: string; arriving: string }> = {
  left: {
    leaving: 'group-[:is(:hover,:focus-visible)]/interactive:-translate-x-[140%]',
    arriving: 'translate-x-full group-[:is(:hover,:focus-visible)]/interactive:translate-x-0',
  },
  right: {
    leaving: 'group-[:is(:hover,:focus-visible)]/interactive:translate-x-[140%]',
    arriving: '-translate-x-full group-[:is(:hover,:focus-visible)]/interactive:translate-x-0',
  },
  'up-right': {
    leaving:
      'group-[:is(:hover,:focus-visible)]/interactive:translate-x-[140%] group-[:is(:hover,:focus-visible)]/interactive:-translate-y-[140%]',
    arriving:
      '-translate-x-full translate-y-full group-[:is(:hover,:focus-visible)]/interactive:translate-x-0 group-[:is(:hover,:focus-visible)]/interactive:translate-y-0',
  },
}

interface SlidingIconProps {
  children: React.ReactNode
  direction?: SlideDirection
}

/**
 * Slides while any `group/interactive` around it is hovered or keyboard-focused. The design-system
 * Button is one; a card whose whole surface is the click target adds the class to opt in.
 *
 * The design system spaces button icons with a negative margin on the svg, which would offset the
 * absolute copy against the one in flow; the margin moves to the slot so both line up.
 */
export function SlidingIcon({ children, direction = 'right' }: SlidingIconProps) {
  const slide = SLIDE[direction]

  return (
    <span className="relative -mx-1 inline-flex overflow-hidden [&_svg]:mx-0">
      <span className={tw(LEAVING, slide.leaving)}>{children}</span>
      <span aria-hidden className={tw('absolute inset-0 inline-flex', ARRIVING, slide.arriving)}>
        {children}
      </span>
    </span>
  )
}
